<template>
  <BaseModule
    v-bind="$props"
    data-module="images-2-columns"
    :style="{ '--bg-color': background }"
  >
    <header
      v-if="title || subtitle"
      class="content base-title-fontsize"
    >
      <component
        :is="titleTag"
        v-if="title"
        class="tit tit-g"
      >
        {{ title }}
      </component>
      <h3
        v-if="subtitle"
        class="sub"
      >
        {{ subtitle }}
      </h3>
    </header>
    <ul class="block-container">
      <li
        v-for="(block, idx) in blocks"
        :key="block.id || idx"
        class="block"
      >
        <LImage
          v-if="block.image"
          :src="block.image"
          :alt="block.alt"
        />
        <div
          v-if="block.title || block.description"
          class="block__overlay base-content-fontsize"
          :class="{ 'hide-title': hideBlockTitles || !block.title }"
          :style="{ background: blockBg, color: textColor }"
        >
          <div class="block__title">
            {{ block.title }}
          </div>
          <div class="block__description">
            {{ block.description }}
          </div>
        </div>
        <EditorLink
          v-if="block.hasLink && block.link && block.link.value"
          :model="block.link"
          class="block__link"
        >
          {{ block.title }}
        </EditorLink>
      </li>
    </ul>
  </BaseModule>
</template>

<script>
import base from '@/mixins/base';
import EditorLink from './EditorLink.vue';
import BaseModule from './generic/BaseModule.vue';

const tinycolor = require('tinycolor2');

export default {
  name: 'Images2Columns',
  components: {
    BaseModule,
    EditorLink,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    titleColor: {
      type: String,
      default: '#000000',
    },
    background: {
      type: String,
      default: '#FFFFFF',
    },
    textColor: {
      type: String,
      default: '#FFFFFF',
    },
    filterColor: {
      type: String,
      default: '#000000',
    },
    opacity: {
      type: Number,
      default: 0.6,
    },
    paddingTop: {
      type: Number,
      default: 20,
    },
    paddingBottom: {
      type: Number,
      default: 20,
    },
    paddingTopMobile: {
      type: Number,
      default: 16,
    },
    paddingBottomMobile: {
      type: Number,
      default: 16,
    },
    hideBlockTitles: {
      type: Boolean,
      default: false,
    },
    blocks: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  computed: {
    blockBg() {
      const color = tinycolor(this.filterColor);

      return color.setAlpha(this.opacity).toString();
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'blocks',
            label: 'Blocos',
            fields: [
              {
                type: 'List',
                name: 'blocks',
                itemLabel: 'Bloco {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar bloco',
                fields: [
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem',
                    helpText: 'Dimensões recomendadas: 920x600',
                    validation: {
                      presence: {
                        allowEmpty: false,
                        message: 'Insira uma imagem para o bloco',
                      },
                    },
                  },
                  {
                    name: 'alt',
                    type: 'ShortText',
                    label: 'Texto alternativo (para SEO)',
                  },
                  {
                    name: 'title',
                    type: 'ShortText',
                    label: 'Título da imagem',
                  },
                  {
                    name: 'description',
                    type: 'LongText',
                    label: 'Descrição',
                  },
                  {
                    name: 'hasLink',
                    type: 'Checkbox',
                    label: 'Incluir link na imagem',
                  },
                  {
                    name: 'link',
                    type: 'Url',
                    isEnabled() {
                      return this.hasLink;
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        name: 'appearance',
        groups: [
          {
            name: 'colors',
            fields: [
              {
                name: 'background', // migrate this field
                type: 'ColorPicker',
                label: 'Cor de fundo do módulo',
              },
              {
                name: 'filterColor',
                type: 'ColorPicker',
                label: 'Cor do filtro nos blocos',
              },
              {
                name: 'opacity',
                type: 'Range',
                label: 'Opacidade',
                min: 0,
                max: 1,
                step: 0.1,
                format: {
                  decimals: 1,
                },
              },
              {
                name: 'hideBlockTitles',
                type: 'Checkbox',
                label: 'Ocultar título dos blocos na visualização inicial',
              },
            ],
            toRemove: ['backgroundColor', 'buttonColor', 'buttonTextColor'],
          },
        ],
      },
    ],
  },
};
</script>

<style lang="scss" scoped>
[data-module='images-2-columns'] {
  overflow: hidden;
  padding: 20px;
  padding-top: var(--padding-top);
  padding-bottom: var(--padding-bottom);

  @include media-breakpoint-down(md) {
    padding-left: 16px;
    padding-right: 16px;
  }

  header {
    margin-bottom: 32px;
  }

  .block-container {
    --gap: 20px;
    margin: calc(var(--gap) / -2);
    display: flex;
    flex-wrap: wrap;

    @include media-breakpoint-down(md) {
      --gap: 16px;
    }

    .block {
      flex: 1 0 calc(50% - var(--gap));
      margin: calc(var(--gap) / 2);
      position: relative;
      overflow: hidden;

      @include media-breakpoint-down(md) {
        flex-basis: calc(100% - var(--gap));
      }

      &:hover {
        .block__overlay {
          height: 100%;
          justify-content: flex-end;

          &.hide-title {
            top: 0;
          }
        }

        .block__description {
          display: block;
        }
      }

      .img {
        display: block;
        width: 100%;
        height: 600px;

        @include media-breakpoint-down(md) {
          height: 440px;
        }
      }

      &__overlay {
        position: absolute;
        bottom: 0;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
        height: 7.6em;
        padding: 30px 48px;
        transition: height 300ms ease-out;

        &.hide-title {
          bottom: initial;
          top: 100%;
          height: 100%;
          transition: top 300ms ease-out;
        }

        @include media-breakpoint-down(lg) {
          padding: 16px;
          height: 84px;
        }

        @include media-breakpoint-down(md) {
          height: 100%;
          justify-content: flex-end;

          &.hide-title {
            top: 0;
          }
        }
      }

      &__title {
        font-size: 1.5em;
        font-weight: $lb-bold;
        line-height: 1.5em;

        @include media-breakpoint-down(lg) {
          font-size: 20px;
        }

        @include media-breakpoint-down(md) {
          font-size: $size-normal;
        }
      }

      &__description {
        display: none;
        font-size: 1em;
        font-weight: $lb-medium;
        line-height: 1.15em;
        margin-top: 10px;

        @include media-breakpoint-down(lg) {
          font-size: $size-small;
        }

        @include media-breakpoint-down(md) {
          display: block;
          font-size: $size-smaller;
        }
      }

      &__link {
        width: 0;
        height: 0;
        font-size: 0;
        display: block;

        &::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 1;
          pointer-events: auto;
          background-color: rgba(0, 0, 0, 0);
        }
      }
    }
  }

  @include media-breakpoint-down(sm) {
    padding: 16px;
    padding-top: var(--padding-top-mobile);
    padding-bottom: var(--padding-bottom-mobile);
  }
}
</style>
